import type { ReactNode } from 'react'
import { FaGithub, FaLinkedin } from 'react-icons/fa'
import { FaXTwitter } from 'react-icons/fa6'
import { SiNpm } from 'react-icons/si'
import { OpenInAI, type OpenInAIAction, type OpenInAIPlatform } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { useLink } from '../../lib/link-context'

interface FooterSmallProps {
  /** Logo as image object (with optional title for icon+text), or plain string text */
  logo?:
    | {
        url: string
        src: string
        alt: string
        /** Optional text displayed next to the logo image */
        title?: string
      }
    | string
  className?: string
  /** Navigation links (e.g., Docs, API, CLI) */
  links?: Array<{ name: string; href: string }>
  socialLinks?: Array<{
    icon: ReactNode
    href: string
    label: string
  }>
  /** Domain name for copyright (e.g., "dotdo.ai") */
  domain?: string
  legalLinks?: Array<{
    name: string
    href: string
  }>
  /** Show "Open in AI" feature next to social icons */
  showOpenInAI?: boolean
  /** OpenInAI variant: 'icons' shows all AI platform icons, 'dropdown' shows a single button */
  openInAIVariant?: 'icons' | 'dropdown'
  /** OpenInAI actions for icons variant */
  openInAIActions?: OpenInAIAction[]
  /** OpenInAI default prompt for dropdown variant */
  openInAIDefaultPrompt?: string
  /** OpenInAI platforms to show */
  openInAIPlatforms?: OpenInAIPlatform[]
  /** Page title for OpenInAI context */
  pageTitle?: string
}

const defaultLinks = [
  { name: 'Docs', href: '#' },
  { name: 'API', href: '#' },
  { name: 'CLI', href: '#' },
  { name: 'SDK', href: '#' },
  { name: 'MCP', href: '#' },
  { name: 'Blog', href: '#' },
]

// X and LinkedIn always point to the .do brand accounts (fixed defaults).
// GitHub and npm are per-product — override them via the `socialLinks` prop.
const defaultSocialLinks = [
  { icon: <FaGithub className="size-5" />, href: '#', label: 'GitHub' },
  { icon: <SiNpm className="size-5" />, href: '#', label: 'npm' },
  { icon: <FaXTwitter className="size-5" />, href: 'https://x.com/dotdo_ai', label: 'X' },
  { icon: <FaLinkedin className="size-5" />, href: 'https://www.linkedin.com/company/dotdo', label: 'LinkedIn' },
]

const defaultLegalLinks = [
  { name: 'Terms', href: '#' },
  { name: 'Privacy', href: '#' },
]

const FooterSmall = ({
  logo = {
    url: '/',
    src: '/logos/mdxui.svg',
    alt: 'Logo',
  },
  links = defaultLinks,
  socialLinks = defaultSocialLinks,
  domain = '.do',
  legalLinks = defaultLegalLinks,
  showOpenInAI = false,
  openInAIVariant = 'dropdown',
  openInAIActions,
  openInAIDefaultPrompt,
  openInAIPlatforms,
  pageTitle,
  className,
}: FooterSmallProps) => {
  const Link = useLink()
  return (
    <section className={cn('pb-12 pt-16 sm:pt-20 lg:pt-24', className)}>
      <div className="container mx-auto max-w-7xl">
        {/* Top row: Logo + links on left, social icons on right */}
        <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-between">
          {/* Left side: Logo + divider + Navigation links */}
          <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
            {/* Logo */}
            {typeof logo === 'string' ? (
              <span className="text-lg font-semibold">{logo}</span>
            ) : (
              <Link href={logo.url} className="flex items-center gap-1.5">
                {logo.src && (
                  <span className="inline-flex rounded-[var(--radius)] bg-accent/50">
                    <img src={logo.src} alt={logo.alt} className="dark:invert" />
                  </span>
                )}
                {logo.title && (
                  <span className="text-lg font-semibold">{logo.title}</span>
                )}
              </Link>
            )}

            {/* Divider (hidden on mobile) */}
            <div className="hidden h-5 w-px bg-border sm:block" />

            {/* Navigation links */}
            <nav>
              <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {links.map((link, idx) => (
                  <li key={idx} className="font-medium hover:text-foreground">
                    <Link href={link.href}>{link.name}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Right side: Social icons + OpenInAI */}
          <div className="flex items-center gap-6">
            <ul className="flex items-center space-x-6 text-muted-foreground">
              {socialLinks.map((social, idx) => (
                <li key={idx} className="font-medium hover:text-foreground">
                  <a href={social.href} aria-label={social.label}>
                    {social.icon}
                  </a>
                </li>
              ))}
            </ul>
            {showOpenInAI && (
              <>
                <div className="h-5 w-px bg-border" />
                <OpenInAI
                  variant={openInAIVariant}
                  actions={openInAIActions}
                  defaultPrompt={openInAIDefaultPrompt}
                  platforms={openInAIPlatforms}
                  pageTitle={pageTitle}
                />
              </>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="my-8 border-t border-border" />

        {/* Bottom row: Copyright and legal links */}
        <div className="flex flex-col items-center justify-between gap-4 text-xs text-muted-foreground sm:flex-row">
          <p>
            © {domain} {new Date().getFullYear()} All rights reserved.
          </p>
          <ul className="flex gap-4">
            {legalLinks.map((link, idx) => (
              <li key={idx} className="hover:text-foreground">
                <Link href={link.href}>{link.name}</Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export { FooterSmall }
export type { FooterSmallProps }
